import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { PAGE_SIZE, type PagedResult } from "../../../../components/PortfolioPerformance/pager";
import { apiGet, apiGetBlob } from "./client";

// Free text from epd_contract.status; the four below are the values the badge styles specially.
export type EpdStatus = string;

export interface EpdContract {
  contract: string;
  office: string;
  industry: string;
  monthsOnBook: number;
  status: EpdStatus;
  exposure: string;
}

/** Downloads every EPD contract at the latest reporting period as a CSV. The backend streams it; the filename is set here
 *  because CORS doesn't expose Content-Disposition cross-origin (same as the APD Monitoring downloads). */
export async function downloadEpdContracts(): Promise<void> {
  const blob = await apiGetBlob("/epd/contracts/download");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "epd_contracts.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface EpdTrend {
  value: string;
  direction: "up" | "down" | "flat";
}

export interface EpdSummary {
  contractsTTM: number;
  /** vs the same month last year */
  contractsTrend: EpdTrend;
  /** vs last month (the first card shows it above the year comparison) */
  contractsTrendMonth: EpdTrend;
  exposureTTM: string;
  /** vs the same month last year */
  exposureTrend: EpdTrend;
  /** The months the trends are measured against, named: last month ("Sep 2026") and the same month last year ("Oct 2025"). */
  vsMonth: string;
  vsYear: string;
}

// null when epd_contract has no rows -- render an empty state, not a fabricated stat.
export function useEpd() {
  return useQuery({
    queryKey: ["epd"],
    queryFn: () => apiGet<EpdSummary | null>("/epd"),
  });
}

// EPD Rate divides by TTM new business from portfolio_performance (a 10 GB table), so it is its own
// request and the other cards never wait on it. null when epd_contract has no rows.
export function useEpdRate() {
  return useQuery({
    queryKey: ["epd", "rate"],
    queryFn: () => apiGet<{ ratePctOfBookings: string; rateTrend?: EpdTrend; vsYear?: string } | null>("/epd/rate"),
  });
}

// The contracts table pages on its own (server-side, 10 rows) -- keepPreviousData keeps the
// current rows on screen while the next page loads.
export function useEpdContracts(offset: number) {
  return useQuery({
    queryKey: ["epd", "contracts", offset],
    queryFn: () =>
      apiGet<PagedResult<EpdContract>>("/epd/contracts", {
        limit: String(PAGE_SIZE),
        offset: String(offset),
      }),
    placeholderData: keepPreviousData,
  });
}
